import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Chip,
} from "@mui/material";
import { AccessTime, Refresh, ExitToApp } from "@mui/icons-material";

interface SessionExpiryModalProps {
  open: boolean;
  onRefresh: () => void;
  onLogout: () => void;
  timeLeft?: number; // minutes left
  isExpired?: boolean;
}

const SessionExpiryModal: React.FC<SessionExpiryModalProps> = ({
  open,
  onRefresh,
  onLogout,
  timeLeft = 0,
  isExpired = false,
}) => {
  const formatTimeLeft = (minutes: number) => {
    if (minutes <= 0) return "Expired";
    if (minutes < 60) return `${minutes} minute${minutes !== 1 ? "s" : ""}`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccessTime color={isExpired ? "error" : "warning"} />
          <Typography variant="h6" component="span">
            {isExpired ? "Session Expired" : "Session Expiring Soon"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <Alert
            severity={isExpired ? "error" : "warning"}
            sx={{ borderRadius: 1 }}
          >
            {isExpired
              ? "Your session has expired for security reasons. Please refresh to continue or logout to return to the login page."
              : "Your session will expire soon due to the 12-hour security limit. You can refresh now to extend your session."}
          </Alert>

          <Box display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" color="text.secondary">
              Session Status:
            </Typography>
            <Chip
              label={
                isExpired ? "Expired" : `${formatTimeLeft(timeLeft)} remaining`
              }
              color={isExpired ? "error" : timeLeft <= 5 ? "error" : "warning"}
              size="small"
              variant="outlined"
            />
          </Box>

          <Typography variant="body2" color="text.secondary">
            <strong>Why does this happen?</strong>
            <br />
            For your security, sessions automatically expire after 12 hours.
            This helps protect your account from unauthorized access.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2 }}>
        <Button
          onClick={onLogout}
          variant="outlined"
          startIcon={<ExitToApp />}
          sx={{
            mr: "auto",
            width: isExpired ? "100%" : "auto",
            height: 30,
            fontSize: 12,
          }}
        >
          Logout
        </Button>

        {!isExpired && (
          <Button
            onClick={onRefresh}
            variant="contained"
            startIcon={<Refresh />}
            autoFocus
            sx={{
              height: 30,
              fontSize: 12,
            }}
          >
            Extend Session
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiryModal;
